import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../lib/cn.js';
import { Skeleton } from './skeleton.js';
// Фон закреплённых ячеек — фон карточки (`Card`), иначе строки просвечивают.
// У первой колонки фон повторяет фон строки при наведении и выделении (иначе
// закреплённая ячейка остаётся «дырой» на подсвеченной строке). Строка со
// своим фоном (шапка группы) задаёт его классом на ячейках: `!bg-…`.
const STICKY_HEADER = '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-[var(--color-surface-raised)] [&_thead_th]:shadow-[inset_0_-1px_0_var(--color-border-default)]';
const STICKY_FIRST_COLUMN = '[&_tr>*:first-child]:sticky [&_tr>*:first-child]:left-0 [&_tr>*:first-child]:z-[1] [&_tr>*:first-child]:bg-[var(--color-surface-raised)] [&_thead_tr>*:first-child]:z-20 ' +
    '[&_tbody_tr:hover>*:first-child]:bg-[var(--color-surface-sunken)] [&_tbody_tr[data-state=selected]>*:first-child]:bg-[var(--color-surface-overlay)]';
/**
 * Доля ширины видимой области, которую могут занять закреплённые колонки.
 * Больше — закрепление отключается целиком (узкий экран): иначе прокручивать
 * было бы нечего.
 */
export const TABLE_PIN_MAX_SHARE = 0.6;
/** Запас у последней колонки (справа) или строки (снизу), когда полоса прокрутки накладная, px. */
export const TABLE_OVERLAY_SCROLLBAR_PAD = 12;
function toggleAttr(el, name, on) {
    if (on) {
        if (!el.hasAttribute(name))
            el.setAttribute(name, '');
    }
    else if (el.hasAttribute(name))
        el.removeAttribute(name);
}
/** Флаги прокрутки по горизонтали — по ним видна тень у края закреплённых. */
function updateScrollFlags(wrap) {
    const suspended = wrap.hasAttribute('data-pins-suspended');
    const rest = wrap.scrollWidth - wrap.clientWidth - wrap.scrollLeft;
    toggleAttr(wrap, 'data-scrolled-x', !suspended && wrap.scrollLeft > 0.5);
    toggleAttr(wrap, 'data-scroll-more-x', !suspended && rest > 0.5);
}
/**
 * Накладная полоса прокрутки (macOS «автоматически», мобильные): места в
 * разметке не занимает и рисуется поверх содержимого. `scrollbar-gutter` на
 * неё не действует — признак: есть вертикальная прокрутка, а ширина полосы 0.
 */
function updateOverlayScrollbar(wrap) {
    const scrollsY = wrap.scrollHeight - wrap.clientHeight > 0.5;
    const overlay = scrollsY && wrap.offsetWidth - wrap.clientWidth <= 0;
    toggleAttr(wrap, 'data-overlay-scroll-y', overlay);
}
/**
 * То же для горизонтальной полосы: есть прокрутка по X, а высота полосы 0 —
 * полоса ложится на последнюю строку (у «Окупаемости» это липкая «Итого»).
 */
function updateOverlayScrollbarX(wrap) {
    const scrollsX = wrap.scrollWidth - wrap.clientWidth > 0.5;
    const overlay = scrollsX && wrap.offsetHeight - wrap.clientHeight <= 0;
    toggleAttr(wrap, 'data-overlay-scroll-x', overlay);
}
/**
 * Смещения закреплённых колонок: ширины ячеек шапки (или первой строки, если
 * шапки нет) суммируются по порядку и пишутся в CSS-переменные обёртки
 * `--tbl-pin-l-<i>` / `--tbl-pin-r-<i>` — без состояния React, без лишних
 * перерисовок. Возвращает измеренные ячейки — за ними следит ResizeObserver.
 */
function updatePinOffsets(wrap, table) {
    let cells = Array.from(table.querySelectorAll(':scope > thead [data-pin]'));
    if (cells.length === 0) {
        const firstRow = table.tBodies[0]?.rows[0];
        cells = firstRow ? Array.from(firstRow.querySelectorAll(':scope > [data-pin]')) : [];
    }
    const widths = { left: [], right: [] };
    for (const cell of cells) {
        const side = cell.getAttribute('data-pin');
        const index = Number(cell.getAttribute('data-pin-index'));
        if ((side !== 'left' && side !== 'right') || !Number.isInteger(index) || index < 0)
            continue;
        const w = cell.getBoundingClientRect().width;
        widths[side][index] = Math.max(widths[side][index] ?? 0, w);
    }
    let total = 0;
    for (const side of ['left', 'right'])
        total += widths[side].reduce((a, w) => a + (w ?? 0), 0);
    // Узкий экран: закреплённые заняли бы почти всё — смещения `auto`, ячейки
    // едут вместе с таблицей. `position: sticky` остаётся: у шапки он держит
    // её сверху при вертикальной прокрутке.
    const suspended = total > 0 && wrap.clientWidth > 0 && total > wrap.clientWidth * TABLE_PIN_MAX_SHARE;
    toggleAttr(wrap, 'data-pins-suspended', suspended);
    for (const side of ['left', 'right']) {
        let offset = 0;
        const list = widths[side];
        for (let i = 0; i < list.length; i++) {
            const name = `--tbl-pin-${side === 'left' ? 'l' : 'r'}-${i}`;
            wrap.style.setProperty(name, suspended ? 'auto' : `${offset}px`);
            offset += list[i] ?? 0;
        }
    }
    return cells;
}
/**
 * Подпись набора колонок и закреплений: `data-col`/`data-pin*` ячеек шапки (или
 * первой строки, если шапки нет). Только чтение атрибутов — без reflow.
 */
function layoutSignature(table) {
    let cells = Array.from(table.querySelectorAll(':scope > thead > tr > *'));
    if (cells.length === 0) {
        const firstRow = table.tBodies[0]?.rows[0];
        cells = firstRow ? Array.from(firstRow.children) : [];
    }
    return cells
        .map((c) => ['data-col', 'data-pin', 'data-pin-index', 'data-pin-edge']
        .map((a) => c.getAttribute(a) ?? '')
        .join(':'))
        .join(',');
}
/**
 * Раскладка таблицы, которую не выразить в CSS: смещения закреплённых колонок,
 * отключение закрепления на узком экране, тени при прокрутке, запас под
 * накладную полосу. Пересчёт — при смене набора/закреплений колонок (до
 * отрисовки кадра) и при изменении размеров обёртки, таблицы и закреплённых ячеек, но только у
 * таблиц с закреплёнными колонками (`data-pin`) или `stickyHeader`.
 */
function useTableLayout(wrapRef, tableRef, stickyHeader) {
    const roRef = React.useRef(null);
    const observed = React.useRef(new Set());
    const stickyHeaderRef = React.useRef(stickyHeader);
    stickyHeaderRef.current = stickyHeader;
    /** Есть ли таблице что считать (закреплённые колонки или липкая шапка). */
    const activeRef = React.useRef(false);
    const measure = React.useCallback(() => {
        const wrap = wrapRef.current;
        const table = tableRef.current;
        if (!wrap || !table)
            return;
        const sticky = stickyHeaderRef.current;
        // Ранний выход: таблица без закрепления и без липкой шапки (инбокс, списки)
        // раскладку не читает — ни reflow, ни атрибутов. `querySelector` раскладку
        // не трогает. Если закрепление только что сняли — убираем следы.
        if (!sticky && !table.querySelector('[data-pin]')) {
            if (activeRef.current) {
                activeRef.current = false;
                for (const name of [
                    'data-pins-suspended',
                    'data-scrolled-x',
                    'data-scroll-more-x',
                    'data-overlay-scroll-y',
                    'data-overlay-scroll-x',
                ])
                    toggleAttr(wrap, name, false);
                for (const el of observed.current)
                    roRef.current?.unobserve(el);
                observed.current.clear();
            }
            return;
        }
        activeRef.current = true;
        const cells = updatePinOffsets(wrap, table);
        updateScrollFlags(wrap);
        // Запас под накладную полосу — только у липкой шапки (см. `stickyHeader`).
        if (sticky)
            updateOverlayScrollbar(wrap);
        else
            toggleAttr(wrap, 'data-overlay-scroll-y', false);
        // Горизонтальная — у любой измеряемой таблицы: прокрутка по X и есть
        // повод для закрепления колонок.
        updateOverlayScrollbarX(wrap);
        const ro = roRef.current;
        if (!ro)
            return;
        const current = new Set(cells);
        for (const el of observed.current) {
            if (!current.has(el)) {
                ro.unobserve(el);
                observed.current.delete(el);
            }
        }
        for (const el of current) {
            if (!observed.current.has(el)) {
                ro.observe(el);
                observed.current.add(el);
            }
        }
    }, [wrapRef, tableRef]);
    // Замер — не на каждый рендер, а при смене набора колонок или закреплений
    // (подпись по `data-col`/`data-pin*` шапки; чтение атрибутов раскладку не
    // трогает) и из ResizeObserver ниже. Эффект без массива зависимостей, потому
    // что состав колонок приходит детьми, а не пропсами.
    const lastSignature = React.useRef(null);
    React.useLayoutEffect(() => {
        const table = tableRef.current;
        if (!table)
            return;
        const signature = `${stickyHeader ? 1 : 0}|${layoutSignature(table)}`;
        if (signature === lastSignature.current)
            return;
        lastSignature.current = signature;
        measure();
    });
    React.useEffect(() => {
        const wrap = wrapRef.current;
        const table = tableRef.current;
        if (!wrap || !table)
            return;
        const onScroll = () => {
            if (activeRef.current)
                updateScrollFlags(wrap);
        };
        wrap.addEventListener('scroll', onScroll, { passive: true });
        const tracked = observed.current;
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(() => measure());
            roRef.current = ro;
            ro.observe(wrap);
            ro.observe(table);
            measure();
        }
        return () => {
            wrap.removeEventListener('scroll', onScroll);
            roRef.current?.disconnect();
            roRef.current = null;
            tracked.clear();
        };
    }, [measure, wrapRef, tableRef]);
}
const TableRoot = React.forwardRef(({ className, density = 'default', stickyHeader, stickyFirstColumn, ...props }, ref) => {
    const wrapRef = React.useRef(null);
    const tableRef = React.useRef(null);
    const setTableRef = React.useCallback((node) => {
        tableRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    useTableLayout(wrapRef, tableRef, Boolean(stickyHeader));
    return (_jsx("div", { ref: wrapRef, "data-table-scroll": "", className: cn('relative w-full overflow-auto', stickyHeader && 'max-h-[75vh] [scrollbar-gutter:stable]'), "data-sticky-header": stickyHeader ? '' : undefined, "data-sticky-first-column": stickyFirstColumn ? '' : undefined, children: _jsx("table", { ref: setTableRef, "data-density": density, className: cn('w-full caption-bottom text-sm text-[var(--color-text-primary)]', stickyHeader && STICKY_HEADER, stickyFirstColumn && STICKY_FIRST_COLUMN, className), ...props }) }));
});
TableRoot.displayName = 'Table';
/**
 * Закреплённая ячейка (`data-pin`): фон карточки, повтор фона строки при
 * наведении и выделении, тень у края закреплённых — только когда под ней есть
 * что прокручивать. Классы активны лишь при `data-pin`, у обычных ячеек их нет.
 */
const PIN_CELL = 'data-[pin]:z-[1] data-[pin]:bg-[var(--color-surface-raised)] ' +
    '[tbody_tr:hover>&[data-pin]]:bg-[var(--color-surface-sunken)] [tbody_tr[data-state=selected]>&[data-pin]]:bg-[var(--color-surface-overlay)] ' +
    '[[data-scrolled-x]_&[data-pin-edge=left]]:after:pointer-events-none [[data-scrolled-x]_&[data-pin-edge=left]]:after:absolute [[data-scrolled-x]_&[data-pin-edge=left]]:after:inset-y-0 [[data-scrolled-x]_&[data-pin-edge=left]]:after:left-full [[data-scrolled-x]_&[data-pin-edge=left]]:after:w-3 [[data-scrolled-x]_&[data-pin-edge=left]]:after:shadow-[inset_10px_0_8px_-8px_rgb(0_0_0/0.16)] ' +
    '[[data-scroll-more-x]_&[data-pin-edge=right]]:after:pointer-events-none [[data-scroll-more-x]_&[data-pin-edge=right]]:after:absolute [[data-scroll-more-x]_&[data-pin-edge=right]]:after:inset-y-0 [[data-scroll-more-x]_&[data-pin-edge=right]]:after:right-full [[data-scroll-more-x]_&[data-pin-edge=right]]:after:w-3 [[data-scroll-more-x]_&[data-pin-edge=right]]:after:shadow-[inset_-10px_0_8px_-8px_rgb(0_0_0/0.16)]';
/**
 * Последняя ячейка строки, когда полоса прокрутки накладная (`data-overlay-scroll-y`
 * ставится только при `stickyHeader`): правый отступ = обычный отступ ячейки +
 * `TABLE_OVERLAY_SCROLLBAR_PAD` (12 px): `px-3` 12 → `pr-6` 24, compact `p-2` 8 →
 * `pr-5` 20. Это ЗАМЕНА правого отступа, а не второй отступ поверх него.
 */
const OVERLAY_SCROLLBAR_PAD = '[[data-overlay-scroll-y]_&:last-child]:pr-6 [[data-overlay-scroll-y]_[data-density=compact]_&:last-child]:pr-5';
/**
 * Ячейки последней строки тела/подвала, когда накладная полоса горизонтальная
 * (`data-overlay-scroll-x`): нижний отступ = обычный + 12 px (`pb-6`, compact
 * `pb-5`) — тоже замена, а не второй отступ. Липкая нижняя строка («Итого»,
 * `sticky bottom-0`) должна быть последней строкой — тогда полоса ложится на
 * этот запас, а не на числа. Шапку не трогает.
 */
const OVERLAY_SCROLLBAR_PAD_X = '[[data-overlay-scroll-x]_:is(tbody,tfoot)>tr:last-child>&]:pb-6 [[data-overlay-scroll-x]_[data-density=compact]_:is(tbody,tfoot)>tr:last-child>&]:pb-5';
function columnCellProps(column, style, withMinWidth) {
    if (!column)
        return style ? { style } : {};
    const minWidth = withMinWidth && column.def.minWidth ? { minWidth: column.def.minWidth } : null;
    if (!column.pin || !column.visible || column.pinIndex < 0) {
        return {
            'data-col': column.id,
            ...(minWidth || style ? { style: { ...minWidth, ...style } } : {}),
        };
    }
    const side = column.pin;
    const offset = `var(--tbl-pin-${side === 'left' ? 'l' : 'r'}-${column.pinIndex}, ${column.pinOffsetFallback}px)`;
    return {
        'data-col': column.id,
        'data-pin': side,
        'data-pin-index': column.pinIndex,
        ...(column.pinEdge ? { 'data-pin-edge': side } : {}),
        style: {
            position: 'sticky',
            [side]: offset,
            ...minWidth,
            ...style,
        },
    };
}
const TableHeader = React.forwardRef(({ className, ...props }, ref) => (_jsx("thead", { ref: ref, className: cn('border-b border-[var(--color-border-default)]', className), ...props })));
TableHeader.displayName = 'Table.Header';
const TableBody = React.forwardRef(({ className, ...props }, ref) => (_jsx("tbody", { ref: ref, className: cn('[&_tr:last-child]:border-0', className), ...props })));
TableBody.displayName = 'Table.Body';
const TableRow = React.forwardRef(({ className, ...props }, ref) => (_jsx("tr", { ref: ref, className: cn('border-b border-[var(--color-border-subtle)] transition-colors hover:bg-[var(--color-surface-sunken)] data-[state=selected]:bg-[var(--color-surface-overlay)]', className), ...props })));
TableRow.displayName = 'Table.Row';
const TableHeaderCell = React.forwardRef(({ className, column, style, ...props }, ref) => (_jsx("th", { ref: ref, className: cn('h-10 px-3 text-left align-middle text-xs font-medium text-[var(--color-text-muted)] [[data-density=compact]_&]:h-8 [[data-density=compact]_&]:px-2', PIN_CELL, '[thead_&[data-pin]]:z-20', OVERLAY_SCROLLBAR_PAD, className), ...columnCellProps(column, style, true), ...props })));
TableHeaderCell.displayName = 'Table.HeaderCell';
const TableCell = React.forwardRef(({ className, column, style, ...props }, ref) => (_jsx("td", { ref: ref, className: cn('p-3 align-middle [[data-density=compact]_&]:p-2', PIN_CELL, OVERLAY_SCROLLBAR_PAD, OVERLAY_SCROLLBAR_PAD_X, className), ...columnCellProps(column, style, false), ...props })));
TableCell.displayName = 'Table.Cell';
const TableCaption = React.forwardRef(({ className, ...props }, ref) => (_jsx("caption", { ref: ref, className: cn('mt-4 text-sm text-[var(--color-text-muted)]', className), ...props })));
TableCaption.displayName = 'Table.Caption';
const TableEmpty = React.forwardRef(({ className, colSpan, children, ...props }, ref) => (_jsx("tr", { ref: ref, ...props, children: _jsx("td", { colSpan: colSpan, className: cn('p-6 text-center text-sm text-[var(--color-text-muted)]', className), children: children ?? 'No data' }) })));
TableEmpty.displayName = 'Table.Empty';
const TableLoading = React.forwardRef(({ colSpan, rows = 3, className, ...props }, ref) => (_jsx(_Fragment, { children: Array.from({ length: rows }).map((_, i) => (_jsx("tr", { ref: i === 0 ? ref : undefined, className: className, ...(i === 0 ? props : {}), children: _jsx("td", { colSpan: colSpan, className: "p-3", children: _jsx(Skeleton, { className: "h-4 w-full rounded-[var(--radius-1)]" }) }) }, i))) })));
TableLoading.displayName = 'Table.Loading';
export const Table = Object.assign(TableRoot, {
    Header: TableHeader,
    Body: TableBody,
    Row: TableRow,
    HeaderCell: TableHeaderCell,
    Cell: TableCell,
    Caption: TableCaption,
    Empty: TableEmpty,
    Loading: TableLoading,
});
//# sourceMappingURL=table.js.map